import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { message } from 'antd';
import { useAuth } from './AuthContext';
import { usePermissions } from './PermissionsContext';
import {
  callCenterAgentsApi,
  callCenterPopApi,
  show422Error,
  ValidationError,
} from '../utils/api';

const CallCenterContext = createContext(null);

export const useCallCenter = () => {
  const ctx = useContext(CallCenterContext);
  if (!ctx) {
    throw new Error('useCallCenter must be used within CallCenterProvider');
  }
  return ctx;
};

/** اختیاری — خارج از Provider خطا نمی‌دهد */
export const useCallCenterOptional = () => useContext(CallCenterContext);

export const CallCenterProvider = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const { isModuleActive } = usePermissions();

  const isCallCenterActive = isAuthenticated && isModuleActive('callcenter');

  const [myAgentExtension, setMyAgentExtension] = useState(null);
  const [myAgentExtensions, setMyAgentExtensions] = useState([]);
  const [agentLoading, setAgentLoading] = useState(false);

  const [screenPopOpen, setScreenPopOpen] = useState(false);
  const [screenPopData, setScreenPopData] = useState(null);
  const [screenPopLoading, setScreenPopLoading] = useState(false);

  const [callingPhone, setCallingPhone] = useState(null);

  const agentLoadedRef = useRef(false);

  const loadMyAgentExtension = useCallback(async (force = false) => {
    if (!isCallCenterActive || !user?.id) return null;
    if (agentLoadedRef.current && !force) return myAgentExtension;
    setAgentLoading(true);
    try {
      const res = await callCenterAgentsApi.getAll();
      const agents = res?.data ?? [];
      const allMine = agents.filter(
        (a) => Number(a.user_id) === Number(user.id) && a.is_active,
      );
      const exts = allMine.map((a) => a.extension).filter(Boolean);
      const ext = exts[0] ?? null;
      setMyAgentExtension(ext);
      setMyAgentExtensions(exts);
      agentLoadedRef.current = true;
      return ext;
    } catch {
      setMyAgentExtension(null);
      setMyAgentExtensions([]);
      return null;
    } finally {
      setAgentLoading(false);
    }
  }, [isCallCenterActive, user?.id, myAgentExtension]);

  const fetchScreenPop = useCallback(
    async ({ phone, extension, silent = false } = {}) => {
      if (!isCallCenterActive) return null;
      if (!silent) setScreenPopLoading(true);
      try {
        const ext = extension ?? myAgentExtension ?? (await loadMyAgentExtension());
        const res = await callCenterPopApi.getScreenPop({
          phone,
          extension: ext || undefined,
        });
        return res?.data ?? null;
      } catch (error) {
        if (!silent) console.error('Screen pop error:', error);
        return null;
      } finally {
        if (!silent) setScreenPopLoading(false);
      }
    },
    [isCallCenterActive, myAgentExtension, loadMyAgentExtension],
  );

  const openScreenPop = useCallback(
    async (params = {}) => {
      const data = params.data ?? (await fetchScreenPop(params));
      if (!data) return;

      // پاپ‌آپ اسکرین‌پاپ فقط برای تماس‌های ورودی است و تماس‌های خروجی نباید پاپ‌آپ داشته باشند
      const direction = String(
        data.direction || data.call?.direction || ''
      ).toLowerCase().trim();

      if (direction && direction !== 'incoming' && direction !== 'inbound') {
        return;
      }

      setScreenPopData(data);
      setScreenPopOpen(true);
    },
    [fetchScreenPop],
  );

  const closeScreenPop = useCallback(() => {
    setScreenPopOpen(false);
    setScreenPopData(null);
  }, []);

  const clickToCall = useCallback(
    async (phone, { extension } = {}) => {
      if (!phone?.trim()) {
        message.warning('شماره تماس موجود نیست');
        return false;
      }
      if (!isCallCenterActive) {
        message.warning('ماژول مرکز تماس فعال نیست');
        return false;
      }

      const normalized = phone.trim();
      setCallingPhone(normalized);
      try {
        const ext = extension ?? myAgentExtension ?? (await loadMyAgentExtension());
        const res = await callCenterPopApi.clickToCall({
          destination: normalized,
          phone: normalized,
          extension: ext || undefined,
        });
        message.success(res?.message || 'درخواست برقراری تماس ارسال شد');
        return true;
      } catch (error) {
        console.error('Click-to-call error:', error);
        if (error instanceof ValidationError) {
          show422Error(error);
        } else {
          message.error(error?.message || 'خطا در برقراری تماس');
        }
        return false;
      } finally {
        setCallingPhone(null);
      }
    },
    [isCallCenterActive, myAgentExtension, loadMyAgentExtension],
  );

  const value = useMemo(
    () => ({
      isCallCenterActive,
      myAgentExtension,
      myAgentExtensions,
      agentLoading,
      loadMyAgentExtension,
      screenPopOpen,
      screenPopData,
      screenPopLoading,
      openScreenPop,
      closeScreenPop,
      fetchScreenPop,
      clickToCall,
      callingPhone,
      isCalling: (phone) => callingPhone != null && callingPhone === phone?.trim(),
    }),
    [
      isCallCenterActive,
      myAgentExtension,
      myAgentExtensions,
      agentLoading,
      loadMyAgentExtension,
      screenPopOpen,
      screenPopData,
      screenPopLoading,
      openScreenPop,
      closeScreenPop,
      fetchScreenPop,
      clickToCall,
      callingPhone,
    ],
  );

  return (
    <CallCenterContext.Provider value={value}>
      {children}
    </CallCenterContext.Provider>
  );
};
